import React, {useRef, useState} from 'react';
import {Tooltip} from 'primereact/tooltip';
import apiClient from "../../../services/api-client";
import CreateNewUser from '../../../features/admin/components/user-management/CreateNewUser'
import EditUser from '../../../features/admin/components/user-management/EditEmployees'
import AppDialog from '../../../components/ui/AppDialog';
import {Toast} from 'primereact/toast';
import {ConfirmPopup, confirmPopup} from 'primereact/confirmpopup';
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import {useMountEffect} from 'primereact/hooks';
import StorageHelper from '../../../utils/storage-helper';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import AppCard from "../../../components/ui/AppCard";

const EmployeesProfileReportTable = () => {
    const [employees, setEmployees] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const dt = useRef(null);
    const toast = useRef(null);
    const [reloadData, setReloadData] = useState(false);
    const [activeUser, setActiveUser] = useState(null)

    const saveUser = async (data) => {
        await apiClient.put('user/edit', data).then((response) => {
            if (response.data.status === "SUCCESS") {
                toast.current.show({
                    severity: 'success',
                    summary: 'Updated',
                    detail: data.isActive ? 'You successfully activated this profile' : 'You successfully disabled the profile',
                    life: 3000
                });
                window.location.reload(true);
            } else {
                toast.current.show({severity: 'warn', summary: 'Failed', detail: response.data.message, life: 3000});
            }
        });
    }

    const handleActive = async (data) => {
        const userPayload = {
            "userRole": {
                "id": data.userRole.id,
            },
            "user": {
                "userId": data.id,
                "emailAddress": data.emailAddress,
                "phoneNumber": data.phoneNumber,
                "firstName": data.firstName,
                "lastName": data.lastName,
                "IDNumber": data.idnumber,
                "isActive": !data.isActive,
                "locked": data.enabled,
                "enabled": data.enabled,
                "userType": data.userType,
                "userRole": data.userRole.id,
            }
        };
        await saveUser(userPayload);
    }

    const handleLock = async (data) => {
        const userPayload = {
            "userRole": {"id": data.userRole.id},
            "user": {
                "userId": data.id,
                "emailAddress": data.emailAddress,
                "phoneNumber": data.phoneNumber,
                "firstName": data.firstName,
                "lastName": data.lastName,
                "IDNumber": data.idnumber,
                "isActive": data.isActive,
                "locked": !data.locked,
                "enabled": data.enabled,
                "userType": data.userType,
                "userRole": data.userRole.id,
            }
        };
        await saveUser(userPayload);
    }

    const rejectFunction = () => {
        toast.current.show({severity: 'warn', summary: 'Rejected', detail: 'You have rejected', life: 3000});
    };

    const cols = [
        {field: 'firstName', header: 'First name'},
        {field: 'lastName', header: 'Last name'},
        {field: 'userRole.name', header: 'User Type'},
        {field: 'emailAddress', header: 'Email'},
        {field: 'phoneNumber', header: 'Phone number'},
        {field: 'actions', header: 'Actions'},
    ];

    useMountEffect(() => {
        fetchUsers();
    }, [reloadData]);

    function renderUserType(rowData) {
        return <span className="badge badge-outline px-3 py-1">{rowData.userRole?.name}</span>;
    }

    function renderActions(rowData) {
        return (
            <div className="actions-container flex gap-2">
                <div className="tooltip" data-tip="Edit Profile">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => {
                            setReloadData(!reloadData);
                            setActiveUser(rowData);
                            setDialogVisible(true);
                        }}
                    >
                        <i className="pi pi-user-edit text-lg"></i>
                    </button>
                </div>

                <div className="tooltip" data-tip={rowData.isActive ? "Disable User" : "Activate User"}>
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={(event) => {
                            confirmPopup({
                                target: event.currentTarget,
                                message: rowData.isActive ? "Are you sure you want to disable this?" : "Are you sure you want to activate this?",
                                icon: "pi pi-exclamation-triangle",
                                accept: () => handleActive(rowData),
                                reject: rejectFunction
                            });
                        }}
                    >
                        <i className={`pi ${rowData.isActive ? 'pi-ban' : 'pi-check-circle'} text-lg`}></i>
                    </button>
                </div>

                <div className="tooltip" data-tip={rowData.isLocked ? "Unlock User" : "Lock User"}>
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={(event) => {
                            confirmPopup({
                                target: event.currentTarget,
                                message: rowData.isLocked ? "Are you sure you want to unlock this?" : "Are you sure you want to lock this?",
                                icon: "pi pi-exclamation-triangle",
                                accept: () => handleLock(rowData),
                                reject: rejectFunction
                            });
                        }}
                    >
                        <i className={`pi ${rowData.isLocked ? 'pi-unlock' : 'pi-lock'} text-lg`}></i>
                    </button>
                </div>
            </div>
        );
    }

    const [dialogVisible, setDialogVisible] = useState(false);
    const exportColumns = cols.map((col) => ({title: col.header, dataKey: col.field}));

    const exportCSV = (selectionOnly) => {
        dt.current.exportCSV({selectionOnly});
    };

    const exportPdf = () => {
        import('jspdf').then((jsPDF) => {
            import('jspdf-autotable').then(() => {
                const doc = new jsPDF.default();
                doc.autoTable(exportColumns, employees);
                doc.save('doves-self-service-employees-profiles-report.pdf');
            });
        });
    };

    const fetchUsers = async () => {
        try {
            await apiClient.get('users/employees', {
                headers: {Authorization: 'Bearer ' + (StorageHelper.get('token'))}
            }).then((response) => {
                if (response.data.status === "SUCCESS") {
                    setEmployees((response.data.responseBody));
                }
            });
        } catch (error) {
        }
    };

    const onGlobalFilter = (event) => {
        setGlobalFilter(event.target.value);
    };

    const globalFilterElement = (
        <div className="relative w-full max-w-xs">
            <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50"></i>
            <input
                type="text"
                className="input input-bordered w-full pl-10 h-10 shadow-sm bg-base-100 rounded-full"
                placeholder="Search employees..."
                value={globalFilter}
                onChange={onGlobalFilter}
            />
        </div>
    );

    const handleCreateUser = async () => {
        setDialogVisible(false)
        await toast.current.show({severity: 'success', summary: 'Success', detail: 'User registered successfully'});
    }

    const header = (
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center w-full">
            {globalFilterElement}
            <div className="flex items-center gap-3 ml-auto">
                <button
                    className="btn btn-primary h-10 min-h-0 text-white font-semibold shadow-sm rounded-full"
                    onClick={() => {
                        setActiveUser(null);
                        setDialogVisible(true);
                    }}
                >
                    <i className="pi pi-user-plus"></i> Create new
                </button>
                <div className="dropdown dropdown-end">
                    <div tabIndex={0} role="button"
                         className="btn btn-outline btn-ghost h-10 min-h-0 shadow-sm rounded-full">
                        <i className="pi pi-file-export"></i> Export
                    </div>
                    <ul tabIndex={0}
                        className="dropdown-content z-[99] menu p-2 shadow-sm bg-base-100 border border-base-200 rounded-box w-52 mt-2">
                        <li><button type="button" onClick={() => exportCSV(false)}><i
                            className="pi pi-file-excel text-success"></i> Download CSV</button></li>
                        <li><button type="button" onClick={() => exportPdf()}><i className="pi pi-file-pdf text-error"></i> Download
                            PDF</button></li>
                    </ul>
                </div>
            </div>
        </div>
    );

    return (
        <div className="w-full">
            <Toast ref={toast}/>

            {activeUser === null ? (
                <AppDialog header="Register new user" visible={dialogVisible} boxClassName="w-11/12 max-w-3xl"
                        onHide={() => {
                            setActiveUser(null)
                            setDialogVisible(false)
                        }}>
                    <CreateNewUser onCloseDialog={handleCreateUser} style={{marginTop: "20px"}}/>
                </AppDialog>
            ) : (
                <AppDialog header="Edit user" visible={dialogVisible} boxClassName="w-11/12 max-w-3xl" onHide={() => {
                    setActiveUser(null)
                    setDialogVisible(false)
                }}>
                    <EditUser employeeData={activeUser} onCloseDialog={handleCreateUser} style={{marginTop: "20px"}}/>
                </AppDialog>
            )}

            <Tooltip target=".export-buttons>button" position="bottom"/>
            <ConfirmPopup/>

            <AppCard title="Employees Profile Report">
                <DataTable
                    paginator
                    rows={5}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    ref={dt}
                    value={employees}
                    header={header}
                    globalFilter={globalFilter}
                    emptyMessage="No employees found"
                    paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                >
                    {cols.map((col, index) => {
                        if (col.field === 'actions') {
                            return <Column key={index} body={renderActions} header={col.header}
                                           style={{textAlign: 'center'}}/>;
                        }
                        if (col.field === 'userRole.name') {
                            return <Column key={index} field={col.field} header={col.header} body={renderUserType}/>;
                        }
                        return <Column key={index} field={col.field} header={col.header}/>;
                    })}
                </DataTable>
            </AppCard>
        </div>
    );
};

export default EmployeesProfileReportTable;
